/**
 * DriveFolderPicker — iter178/179/181
 *
 * Three ways to pick a Drive folder, exposed as three trigger buttons:
 *
 * (a) "Auto-detect root" (iter181) — searches Drive for the canonical
 *     subfolder names using only the saved API key, then ranks each
 *     candidate parent by how many of the four canonical subfolders it
 *     contains. Works only when those folders are publicly shared
 *     ("anyone with the link can view"), since a bare API key can't
 *     enumerate private Drive content.
 *
 * (b) "Pick from my Drive" — opens the native Google Picker. Uses
 *     Google Identity Services (GIS) to mint a short-lived
 *     ``drive.readonly`` access token in the browser, then loads the
 *     Picker JS and shows a folders-only view. Requires
 *     ``picker_oauth_client_id`` to be configured server-side.
 *
 * (c) "Browse with API key" — server-side mini-browser. Lists Drive
 *     subfolders of a given folder ID using the saved API key. Works
 *     for folders shared with "anyone with the link" — same constraint
 *     the existing API-key flow already relies on, so no new
 *     credentials needed.
 *
 * Selecting a folder calls ``onPick({ id, name })`` and closes the
 * surrounding UI.
 */
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CloudArrowDown, FolderOpen, MagnifyingGlass, X, House, CaretRight, MagicWand, CheckCircle, Warning } from "@phosphor-icons/react";
import { browseDriveFolder, getDrivePickerCredentials, autodetectDriveRoot } from "@/lib/api";

const GIS_URL = "https://accounts.google.com/gsi/client";
const PICKER_URL = "https://apis.google.com/js/api.js";

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (typeof document === "undefined") return reject(new Error("no document"));
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === "1") return resolve();
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error(`failed to load ${src}`)), { once: true });
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.defer = true;
    s.onload = () => { s.dataset.loaded = "1"; resolve(); };
    s.onerror = () => reject(new Error(`failed to load ${src}`));
    document.head.appendChild(s);
  });
}

export default function DriveFolderPicker({ user, pickerAvailable, hasApiKey, onPick }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showBrowser, setShowBrowser] = useState(false);
  const [showAuto, setShowAuto] = useState(false);

  const onClickGooglePicker = async () => {
    setErr(""); setBusy(true);
    try {
      const creds = await getDrivePickerCredentials(user.email);
      if (!creds?.available || !creds.oauth_client_id || !creds.api_key) {
        throw new Error("Google Picker is not configured. Paste an OAuth Client ID below and save first.");
      }
      await Promise.all([loadScript(GIS_URL), loadScript(PICKER_URL)]);
      await new Promise((resolve, reject) => {
        if (!window.gapi) return reject(new Error("gapi failed to load"));
        window.gapi.load("picker", { callback: resolve, onerror: () => reject(new Error("gapi picker failed")) });
      });
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: creds.oauth_client_id,
        scope: "https://www.googleapis.com/auth/drive.readonly",
        callback: (resp) => {
          if (resp.error) {
            setBusy(false);
            setErr(`Google sign-in failed: ${resp.error}`);
            return;
          }
          const accessToken = resp.access_token;
          const view = new window.google.picker.DocsView(window.google.picker.ViewId.FOLDERS)
            .setIncludeFolders(true)
            .setSelectFolderEnabled(true)
            .setMimeTypes("application/vnd.google-apps.folder");
          const builder = new window.google.picker.PickerBuilder()
            .addView(view)
            .setOAuthToken(accessToken)
            .setDeveloperKey(creds.api_key)
            .setCallback((data) => {
              const action = data[window.google.picker.Response.ACTION];
              if (action === window.google.picker.Action.PICKED) {
                const docs = data[window.google.picker.Response.DOCUMENTS] || [];
                const folder = docs.find((d) => d.mimeType === "application/vnd.google-apps.folder");
                if (folder) {
                  onPick?.({ id: folder.id, name: folder.name });
                }
              }
            });
          if (creds.app_id) builder.setAppId(creds.app_id);
          builder.build().setVisible(true);
          setBusy(false);
        },
      });
      tokenClient.requestAccessToken({ prompt: "consent" });
    } catch (e) {
      setBusy(false);
      const detail = e?.response?.data?.detail || e?.message || "Could not open Google Picker";
      setErr(detail);
    }
  };

  return (
    <>
      <div className="flex flex-wrap gap-2 pt-1" data-testid="drive-folder-picker-buttons">
        <Button type="button"
                  data-testid="drive-picker-autodetect"
                  onClick={() => setShowAuto(true)}
                  disabled={!hasApiKey}
                  title={hasApiKey
                              ? "Search Drive for the canonical subfolders and pick their common parent"
                              : "Save the API key first; the auto-detect needs it to search Drive"}
                  className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 px-3 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
          <MagicWand size={14} weight="bold" />
          Auto-detect root
        </Button>
        <Button type="button"
                  data-testid="drive-picker-google"
                  onClick={onClickGooglePicker}
                  disabled={busy || !pickerAvailable}
                  title={pickerAvailable
                              ? "Sign in to Google and click your folder"
                              : "Paste a Google OAuth Client ID below and save to enable"}
                  className="rounded-sm bg-white border border-blue-300 text-blue-800 hover:bg-blue-50 h-9 px-3 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
          <CloudArrowDown size={14} weight="bold" />
          {busy ? "Opening…" : "Pick from my Drive"}
        </Button>
        <Button type="button"
                  data-testid="drive-picker-browse"
                  onClick={() => setShowBrowser(true)}
                  className="rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 h-9 px-3 text-sm font-semibold inline-flex items-center gap-1.5">
          <FolderOpen size={14} weight="bold" />
          Browse with API key
        </Button>
      </div>
      {err && (
        <div data-testid="drive-picker-error"
              className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-2 py-1.5">
          {err}
        </div>
      )}
      {showBrowser && (
        <DriveBrowserModal
          user={user}
          onClose={() => setShowBrowser(false)}
          onPick={(folder) => {
            setShowBrowser(false);
            onPick?.(folder);
          }}
        />
      )}
      {showAuto && (
        <AutoDetectRootModal
          user={user}
          onClose={() => setShowAuto(false)}
          onPick={(folder) => {
            setShowAuto(false);
            onPick?.(folder);
          }}
        />
      )}
    </>
  );
}


function AutoDetectRootModal({ user, onClose, onPick }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const run = async () => {
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await autodetectDriveRoot(user.email);
      setResult(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Auto-detect failed");
    } finally { setBusy(false); }
  };

  // Auto-run on open so the operator doesn't have to click twice.
  useEffect(() => { run(); }, []);

  const candidates = result?.candidates || [];

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center" data-testid="drive-autodetect-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white rounded-sm shadow-xl border border-slate-200 max-h-[80vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <MagicWand size={16} weight="bold" className="text-blue-700" />
            <span className="font-bold text-sm text-slate-900">Auto-detect Drive root folder</span>
          </div>
          <button data-testid="drive-autodetect-close"
                   onClick={onClose}
                   className="w-7 h-7 flex items-center justify-center rounded-sm border border-slate-200 hover:bg-slate-100 text-slate-600">
            <X size={13} />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <p className="text-[12px] text-slate-600">
            Searches Drive for the canonical subfolders (<code className="font-mono bg-slate-100 px-1 rounded-sm">Parts_Files</code>,{" "}
            <code className="font-mono bg-slate-100 px-1 rounded-sm">Pictures</code>,{" "}
            <code className="font-mono bg-slate-100 px-1 rounded-sm">Vendor_Shipments_Files</code>,{" "}
            <code className="font-mono bg-slate-100 px-1 rounded-sm">Vendor_Shipments_Images</code>) using the saved API key, then ranks each candidate parent by how many of those subfolders it contains.
          </p>
          <p className="text-[11px] text-slate-500">
            Works only when those folders are shared as <b>&quot;anyone with the link can view&quot;</b> — Google reserves search across private Drive content for OAuth. If you see zero candidates, use <b>Pick from my Drive</b> or <b>Browse with API key</b> instead.
          </p>

          {busy && (
            <div className="text-[13px] text-slate-700" data-testid="drive-autodetect-running">
              Searching Drive…
            </div>
          )}

          {err && (
            <div data-testid="drive-autodetect-error"
                  className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-2 py-1.5">
              {err}
            </div>
          )}

          {result && (
            <div className="space-y-2" data-testid="drive-autodetect-result">
              <div className={"text-[12px] px-2 py-1.5 rounded-sm border "
                                + (candidates.length > 0
                                      ? "text-slate-700 bg-slate-50 border-slate-200"
                                      : "text-amber-800 bg-amber-50 border-amber-200")}>
                {result.note}
              </div>

              {candidates.length > 0 && (
                <ul className="border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-[40vh] overflow-y-auto">
                  {candidates.map((c, i) => {
                    const best = i === 0 && c.score >= 2;
                    return (
                      <li key={c.root_id}
                            data-testid={`drive-autodetect-row-${c.root_id}`}
                            className={"px-3 py-2 flex items-start gap-2 " + (best ? "bg-emerald-50/50" : "")}>
                        <div className="shrink-0 mt-0.5">
                          {best
                            ? <CheckCircle size={14} weight="fill" className="text-emerald-600" />
                            : <Warning size={14} weight="fill" className="text-amber-500" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-bold text-slate-900 truncate">
                            {c.root_name || "(unnamed folder)"}
                            {best && <span className="ml-2 text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-sm">Best match</span>}
                          </div>
                          <div className="text-[11px] font-mono text-slate-500 truncate">{c.root_id}</div>
                          <div className="text-[11px] text-slate-600 mt-0.5">
                            Contains {c.score} of 4 canonical subfolders:{" "}
                            <span className="font-mono">{Object.keys(c.matched_subfolders).join(", ") || "—"}</span>
                          </div>
                        </div>
                        <Button type="button"
                                  data-testid={`drive-autodetect-use-${c.root_id}`}
                                  onClick={() => onPick?.({ id: c.root_id, name: c.root_name || c.root_id })}
                                  className="shrink-0 rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-7 px-2 text-[11px] font-semibold">
                          Use this
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}

              <div className="pt-1">
                <Button type="button"
                          data-testid="drive-autodetect-rerun"
                          onClick={run}
                          disabled={busy}
                          className="rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 h-8 px-3 text-[12px] font-semibold">
                  Re-run
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function DriveBrowserModal({ user, onClose, onPick }) {
  // ``path`` is a breadcrumb of {id, name} from root to current.
  const [path, setPath] = useState([]);
  const [currentId, setCurrentId] = useState("");
  const [currentName, setCurrentName] = useState("");
  const [subfolders, setSubfolders] = useState([]);
  const [seedId, setSeedId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 30); }, []);

  const load = async (folderId, breadcrumb) => {
    setBusy(true); setErr("");
    try {
      const r = await browseDriveFolder(user.email, folderId);
      setCurrentId(r.folder_id);
      setCurrentName(r.folder_name || folderId);
      setSubfolders(r.subfolders || []);
      setPath(breadcrumb);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Browse failed";
      setErr(detail);
    } finally { setBusy(false); }
  };

  const start = async () => {
    const id = seedId.trim();
    if (!id) { setErr("Paste a starting Drive folder ID"); return; }
    await load(id, [{ id, name: id }]);
  };

  const drillIn = async (folder) => {
    await load(folder.id, [...path, { id: folder.id, name: folder.name }]);
  };

  const goTo = async (idx) => {
    const newPath = path.slice(0, idx + 1);
    const target = newPath[newPath.length - 1];
    await load(target.id, newPath);
  };

  const pickCurrent = () => {
    if (!currentId) return;
    onPick?.({ id: currentId, name: currentName || currentId });
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center" data-testid="drive-browser-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white rounded-sm shadow-xl border border-slate-200 max-h-[80vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <FolderOpen size={16} weight="bold" className="text-slate-700" />
            <span className="font-bold text-sm text-slate-900">Browse Drive folders</span>
          </div>
          <button data-testid="drive-browser-close"
                   onClick={onClose}
                   className="w-7 h-7 flex items-center justify-center rounded-sm border border-slate-200 hover:bg-slate-100 text-slate-600">
            <X size={13} />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto">
          {!currentId && (
            <div className="space-y-2">
              <p className="text-[12px] text-slate-600">
                Paste any Drive folder ID you can access (anyone-with-link or your own folders shared with the API key) to start browsing. Drill into subfolders, then click <b>Use this folder</b>.
              </p>
              <div className="flex gap-2">
                <Input ref={inputRef}
                          data-testid="drive-browser-seed"
                          value={seedId}
                          onChange={(e) => setSeedId(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && start()}
                          placeholder="paste a Drive folder ID"
                          className="h-9 text-sm font-mono rounded-sm border-slate-300" />
                <Button type="button"
                          data-testid="drive-browser-start"
                          onClick={start}
                          disabled={busy || !seedId.trim()}
                          className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 px-3 text-sm font-semibold inline-flex items-center gap-1.5">
                  <MagnifyingGlass size={13} weight="bold" /> Start
                </Button>
              </div>
            </div>
          )}

          {currentId && (
            <div className="flex items-center gap-1 flex-wrap text-[12px] text-slate-600"
                  data-testid="drive-browser-breadcrumb">
              <button onClick={() => { setPath([]); setCurrentId(""); setSubfolders([]); }}
                       className="inline-flex items-center gap-1 text-blue-700 hover:underline">
                <House size={12} weight="bold" /> root
              </button>
              {path.map((p, i) => (
                <span key={`${p.id}-${i}`} className="inline-flex items-center gap-1">
                  <CaretRight size={11} className="text-slate-400" />
                  <button onClick={() => goTo(i)}
                            className={"font-mono truncate max-w-[14ch] " + (i === path.length - 1
                              ? "text-slate-900 font-bold"
                              : "text-blue-700 hover:underline")}>
                    {p.name || p.id}
                  </button>
                </span>
              ))}
            </div>
          )}

          {err && (
            <div data-testid="drive-browser-error"
                  className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-2 py-1.5">
              {err}
            </div>
          )}

          {currentId && (
            <div className="border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-[40vh] overflow-y-auto"
                  data-testid="drive-browser-subfolders">
              {busy && <div className="text-xs text-slate-400 px-3 py-3">Loading…</div>}
              {!busy && subfolders.length === 0 && (
                <div className="text-xs text-slate-400 px-3 py-3"
                      data-testid="drive-browser-empty">
                  No subfolders in this folder. Click <b>Use this folder</b> below if this is the one you want.
                </div>
              )}
              {!busy && subfolders.map((f) => (
                <button key={f.id}
                         data-testid={`drive-browser-row-${f.id}`}
                         onClick={() => drillIn(f)}
                         className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-blue-50 text-sm text-slate-800">
                  <FolderOpen size={14} weight="fill" className="text-amber-500 shrink-0" />
                  <span className="truncate flex-1">{f.name}</span>
                  <span className="text-[10px] font-mono text-slate-400 truncate max-w-[14ch]">{f.id}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="px-4 py-2.5 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-2">
          <div className="text-[11px] text-slate-500 truncate">
            {currentId
              ? <>Current: <span className="font-mono text-slate-700">{currentName || currentId}</span></>
              : <>Paste a folder ID to start.</>}
          </div>
          <Button type="button"
                  data-testid="drive-browser-pick"
                  onClick={pickCurrent}
                  disabled={!currentId}
                  className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 px-3 text-sm font-semibold disabled:opacity-50">
            Use this folder
          </Button>
        </div>
      </div>
    </div>
  );
}
